import React, { useState } from 'react';
import { Alert, Modal, Tooltip } from 'antd';
import { ModalProps } from 'antd/lib/modal';
import { Button, Upload } from '@digital/ui';
import {
  baixarModeloOrganogramaGestores,
  importarOrganogramaGestores,
  ImportacaoOrganogramaResult,
} from '@app/api/organogramaGestor';

const ImportModal = Modal as React.ComponentType<React.PropsWithChildren<ModalProps>>;

const ImportarPlanilha = ({ onImported }: { onImported: () => void }) => {
  const [visible, setVisible] = useState(false);
  const [file, setFile] = useState<File>();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ImportacaoOrganogramaResult>();
  const [error, setError] = useState<string>();
  const [downloading, setDownloading] = useState(false);

  const baixarModelo = async () => {
    setDownloading(true);
    setError(undefined);
    try {
      await baixarModeloOrganogramaGestores();
    } catch (e) {
      setError('Não foi possível baixar o template. Tente novamente.');
    } finally {
      setDownloading(false);
    }
  };

  const executar = async (confirmar: boolean) => {
    if (!file || loading) return;
    setLoading(true);
    setError(undefined);
    try {
      const resposta = await importarOrganogramaGestores(file, confirmar);
      if (!resposta) throw new Error('Resposta indisponível');
      setResult(resposta);
      if (resposta.importado) onImported();
    } catch (e) {
      setResult(undefined);
      setError('Não foi possível processar a planilha. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Tooltip title="Importar planilha">
        <Button
          icon="ui-upload"
          aria-label="Importar planilha"
          onClick={() => {
            setFile(undefined);
            setResult(undefined);
            setError(undefined);
            setVisible(true);
          }}
        />
      </Tooltip>
      <ImportModal
        title="Importar organograma de gestores"
        visible={visible}
        width={640}
        destroyOnClose
        closable={!loading}
        maskClosable={!loading}
        onCancel={() => {
          if (!loading) setVisible(false);
        }}
        footer={[
          <Button key="fechar" type="link" disabled={loading} onClick={() => setVisible(false)}>
            FECHAR
          </Button>,
          <Button
            key="validar"
            loading={loading}
            disabled={!file || !!(result && result.importado)}
            onClick={() => executar(false)}>
            VALIDAR PLANILHA
          </Button>,
          <Button
            key="importar"
            type="primary"
            loading={loading}
            disabled={!result || !result.valid || result.importado}
            onClick={() => executar(true)}>
            CONFIRMAR IMPORTAÇÃO
          </Button>,
        ]}>
        <Button
          type="link"
          loading={downloading}
          disabled={loading}
          onClick={baixarModelo}
          style={{ paddingLeft: 0, marginBottom: 16 }}>
          BAIXAR TEMPLATE
        </Button>
        <div style={{ marginBottom: 24 }}>
          <Upload
            type="select"
            accept=".xlsx"
            disabled={loading}
            fileList={file ? [{ uid: 'organograma-planilha', name: file.name, size: file.size, type: file.type }] : []}
            beforeUpload={selectedFile => {
              setResult(undefined);
              setError(undefined);
              if (!/\.xlsx$/i.test(selectedFile.name) || selectedFile.size > 10 * 1024 * 1024) {
                setFile(undefined);
                setError('Selecione uma planilha .xlsx de até 10 MB.');
                return false;
              }
              setFile(selectedFile);
              return false;
            }}
            onChange={() => undefined}
            onRemove={() => {
              setFile(undefined);
              setResult(undefined);
              setError(undefined);
              return true;
            }}
          />
        </div>
        {error && <Alert type="error" message={error} showIcon />}
        {result && (!result.valid || result.importado) && (
          <div aria-live="polite">
            <Alert
              showIcon
              type={!result.valid ? 'error' : 'success'}
              message={!result.valid ? 'Corrija os erros e valide a planilha novamente.' : 'Importação concluída.'}
            />
            {result.notifications && result.notifications.length > 0 && (
              <ul style={{ maxHeight: 240, overflowY: 'auto', marginTop: 16 }}>
                {result.notifications.map((item, index) => (
                  <li key={index}>{item.message}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </ImportModal>
    </>
  );
};

export default ImportarPlanilha;
